import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  Assessment as AssessmentIcon,
  CheckCircle,
  Info,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ClientLayout } from "../../components/layout/ClientLayout";
import { useAuth } from "../../contexts/AuthContext";
import { ProjectOverview } from "../../components/ProjectOverview";
import ApprovalCard from "../../components/ApprovalCard";
import { useClientProjects } from "./useClientProjects";
import { useClientApprovals } from "./useClientApprovals";

export function ClientDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(
    null,
  );

  const { projects, loading: projectsLoading } = useClientProjects(
    profile?.id || "",
  );
  const {
    approvals,
    loading: approvalsLoading,
    fetchApprovals,
  } = useClientApprovals(profile?.id || "");

  const pendingApprovals = approvals.filter((a) => a.status === "pending");
  const respondedApprovals = approvals.filter((a) => a.status !== "pending");

  const selectedApproval = approvals.find((a) => a.id === selectedApprovalId);

  const handleStatusChanged = async () => {
    // Explicitly refetch to ensure UI updates
    await fetchApprovals();
    setSelectedApprovalId(null);
  };

  const features = [
    {
      title: "Real-time Updates",
      description:
        "Get notified when your contractor posts progress updates with photos.",
      icon: <NotificationsIcon sx={{ fontSize: 32 }} />,
      color: "#2563eb",
    },
    {
      title: "Easy Approvals",
      description:
        "Review and approve change orders or material selections with one click.",
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
      color: "#10b981",
    },
    {
      title: "Direct Communication",
      description:
        "Message your contractor directly within the app for quick questions.",
      icon: <ChatIcon sx={{ fontSize: 32 }} />,
      color: "#f59e0b",
    },
    {
      title: "Track Progress",
      description:
        "View milestones and see exactly how far along your project is.",
      icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
      color: "#8b5cf6",
    },
  ];

  if (projectsLoading || approvalsLoading) {
    return (
      <ClientLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </ClientLayout>
    );
  }

  const hasProjects = projects.length > 0;

  return (
    <ClientLayout>
      <Box sx={{ mb: 4 }}>
        {/* Welcome Section */}
        <Card
          elevation={0}
          sx={{ mb: 3, bgcolor: "primary.main", color: "white" }}
        >
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "white",
                  color: "primary.main",
                }}
              >
                {profile?.name?.charAt(0) || "C"}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Welcome, {profile?.name || "Client"}!
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                  Stay updated on your construction project progress.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {!hasProjects && (
          <>
            {/* Empty State - No Projects */}
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ py: 8, textAlign: "center" }}>
                <Box
                  sx={{
                    bgcolor: "grey.100",
                    borderRadius: "50%",
                    width: 80,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <HomeIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  No active projects
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 500, mx: "auto" }}
                >
                  You haven't been added to any projects yet. Your contractor
                  will send you an invitation to join your project.
                </Typography>
                <Card
                  sx={{
                    maxWidth: 600,
                    mx: "auto",
                    bgcolor: "info.lighter",
                    borderLeft: 4,
                    borderColor: "info.main",
                  }}
                >
                  <CardContent
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Info color="info" />
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Waiting for invitation?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Check your email for a project invite from your
                        contractor.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Feature Cards */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              What you can do with BuildBridge
            </Typography>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{
                          bgcolor: `${feature.color}15`,
                          color: feature.color,
                          borderRadius: 2,
                          p: 1.5,
                          width: "fit-content",
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {hasProjects && (
          <>
            {/* Projects Section */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Your Projects
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {projects.map((project) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={project.id}>
                  <ProjectOverview
                    project={project}
                    onClick={() => navigate(`/client/projects/${project.id}`)}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Approvals Section */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    mb: 3,
                  }}
                >
                  <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                  >
                    <Tab
                      label={`Pending Approvals (${pendingApprovals.length})`}
                    />
                    <Tab label={`Responded (${respondedApprovals.length})`} />
                  </Tabs>
                </Box>

                {/* Pending Approvals Tab */}
                {currentTab === 0 && (
                  <Stack spacing={3}>
                    {pendingApprovals.length === 0 && (
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <CheckCircle
                          sx={{
                            fontSize: 64,
                            color: "success.main",
                            mb: 2,
                          }}
                        />
                        <Typography variant="h6" gutterBottom>
                          All caught up!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          No pending approvals at this time.
                        </Typography>
                      </Box>
                    )}
                    {pendingApprovals.map((approval) => (
                      <Box
                        key={approval.id}
                        onClick={() => setSelectedApprovalId(approval.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <ApprovalCard
                          approval={approval}
                          projectId={approval.project_id}
                          contractorId={approval.contractor_id}
                          currentUserId={profile?.id}
                          currentUserRole="client"
                          currentUserName={profile?.name || "You"}
                          showActions={false}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Responded Approvals Tab */}
                {currentTab === 1 && (
                  <Stack spacing={3}>
                    {respondedApprovals.length === 0 && (
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <Info
                          sx={{
                            fontSize: 64,
                            color: "text.secondary",
                            mb: 2,
                          }}
                        />
                        <Typography variant="h6" gutterBottom>
                          No responded approvals
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Approvals you've responded to will appear here.
                        </Typography>
                      </Box>
                    )}
                    {respondedApprovals.map((approval) => (
                      <Box
                        key={approval.id}
                        onClick={() => setSelectedApprovalId(approval.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <ApprovalCard
                          approval={approval}
                          projectId={approval.project_id}
                          contractorId={approval.contractor_id}
                          currentUserId={profile?.id}
                          currentUserRole="client"
                          currentUserName={profile?.name || "You"}
                          showActions={false}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      {/* Approval Detail Dialog */}
      <Dialog
        open={!!selectedApprovalId}
        onClose={() => setSelectedApprovalId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Approval Details</Typography>
            <IconButton
              onClick={() => setSelectedApprovalId(null)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedApproval && (
            <ApprovalCard
              approval={selectedApproval}
              projectId={selectedApproval.project_id}
              contractorId={selectedApproval.contractor_id}
              currentUserId={profile?.id}
              currentUserRole="client"
              currentUserName={profile?.name || "You"}
              showActions={true}
              onStatusChanged={handleStatusChanged}
            />
          )}
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
}
